import { cloneElement, useEffect, useRef } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Text';
import colors from '@/config/colors';
import { IS_WEB } from '@/utils/platform';

const styles = StyleSheet.create({
  base: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 8,
    gap: 4,
  },
  controlGroupBase: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  controlBase: {
    backgroundColor: '#3a3a3a',
    height: 40,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    borderRadius: 8,
    paddingHorizontal: 14,
  },
  controlText: {
    color: colors.white,
    fontWeight: '600',
  },
  highlighted: {
    backgroundColor: colors.primary,
  },
  disabled: {
    opacity: 0.4,
  },
  pressed: {
    opacity: 0.6,
  },
});

export function ExerciseControl({
  backgroundColor,
  highlighted,
  isDark,
  label,
  icon,
  onPress,
  ...props
}) {
  const containerMaxWidth = 280;
  const targetTextRef = useRef();

  const fontSize = 18;

  const dynamicColor = {
    text: {
      color: backgroundColor ? colors.white : isDark ? colors.white : '#3a3a3a',
    },
    base: {
      backgroundColor: backgroundColor
        ? backgroundColor
        : isDark
          ? '#3a3a3a'
          : colors.white,
    },
  };

  /**
   * Adjust font size to fit container does not work on web, so we need a fallback there
   */

  useEffect(() => {
    if (!IS_WEB) {
      return;
    }
    const targetText = targetTextRef.current;
    if (!targetText) {
      return;
    }
    targetText.style.fontSize = `${fontSize}px`;
    const width = targetText?.getBoundingClientRect()?.width;
    if (width === containerMaxWidth) {
      targetText.style.fontSize = '1vw';
    }
  }, [label]);

  const pressProps = onPress
    ? { onPressIn: onPress }
    : {
        onPressIn: props.onPressIn,
        onPressOut: props.onPressOut,
      };

  return (
    <Pressable
      {...pressProps}
      disabled={props.isDisabled}
      style={({ pressed }) =>
        props.isDisabled
          ? styles.disabled
          : pressed
            ? styles.pressed
            : undefined
      }
    >
      <View
        style={[
          styles.controlBase,
          dynamicColor.base,
          highlighted && styles.highlighted,
        ]}
      >
        {!!icon &&
          cloneElement(icon, {
            size: 18,
            style: dynamicColor.text,
          })}
        {!!label && (
          <Text style={[styles.controlText, dynamicColor.text, { fontSize }]}>
            {label}
          </Text>
        )}
      </View>
    </Pressable>
  );
}

export function ControlGroup({ children }) {
  return <View style={styles.controlGroupBase}>{children}</View>;
}

export function ExerciseControls({ children }) {
  return <View style={styles.base}>{children}</View>;
}
